import "reflect-metadata";

/**
 * Utility functions for metadata reflection and external key lookups
 */

/**
 * Get property value from an object by matching against external key metadata
 * 
 * @param obj - The object to search
 * @param key - The external key to match against (e.g., route path)
 * @returns The property value if found, undefined otherwise
 * 
 * @example
 * // Find app by route path
 * const app = getByExternalKey(availableApps, 'dashboard');
 * // Returns: availableApps.dashboardApp value
 */
export function getByExternalKey<T extends object>(
  obj: T,
  key: string
): T[keyof T] | undefined {
  const proto = Object.getPrototypeOf(obj);

  for (const prop of Object.keys(obj)) {
    const meta = Reflect.getMetadata("externalKey", proto, prop);
    if (meta === key || prop === key) {
      return (obj as any)[prop];
    }
  }
  return undefined;
}

/**
 * Get external key metadata for a given property
 * 
 * @param obj - The object containing the property
 * @param prop - The property name to lookup
 * @returns The external key from metadata, or the property name if no metadata exists
 * 
 * @example
 * // Get route path for a property
 * const routePath = getExternalKey(availableApps, 'dashboardApp');
 * // Returns: 'dashboard' (from metadata)
 */
export function getExternalKey<T extends object>(
  obj: T,
  prop: keyof T
): string {
  const proto = Object.getPrototypeOf(obj);
  const meta = Reflect.getMetadata("externalKey", proto, prop as string);
  return meta ?? (prop as string);
}


/**
 * Decorator to define an external key for a property that differs from the property name
 * 
 * @param name - The external key to associate with the property (e.g., route path)
 * 
 * @example
 * class AvailableApps {
 *   @Key('service-matrix')
 *   servicesMatrix = signal(true);
 * }
 * 
 * // Later use with getByExternalKey
 * const app = getByExternalKey(availableApps, 'service-matrix');
 */
export const Key = (name: string) =>
  (target: any, propertyKey: string) => {
    Reflect.defineMetadata("externalKey", name, target, propertyKey);
  };